import React from 'react';

interface IUseScrollRestorationOptions {
    treeListRef: React.RefObject<any>;
    dataLength: number;
    isLoading?: boolean;
}

const SCROLL_STORAGE_KEY = 'catalogueScrollPosition';
const SHOULD_RESTORE_KEY = 'catalogueShouldRestore';
const DOM_CHECK_INTERVAL = 50;
const DOM_STABILIZE_DELAY = 100;

export const useScrollRestoration = ({
    treeListRef,
    dataLength,
    isLoading = false
}: IUseScrollRestorationOptions) => {

    // Save scroll position to localStorage before navigation
    const saveScrollPosition = React.useCallback(
        (rowKey?: string | number) => {
            if (treeListRef.current?.instance) {
                try {
                    const scrollTop = treeListRef.current.instance.getScrollable().scrollTop();
                    localStorage.setItem(SCROLL_STORAGE_KEY, scrollTop.toString());
                    localStorage.setItem(SHOULD_RESTORE_KEY, 'true');
                    if (rowKey) {
                        localStorage.setItem('catalogueSelectedRow', rowKey.toString());
                    }
                } catch (err) {
                    // Silently handle errors - scroll position saving is not critical
                }
            }
        },
        [treeListRef]);

    // Reset scroll state - clear localStorage
    const resetScrollState = React.useCallback(
        () => {
            localStorage.removeItem(SCROLL_STORAGE_KEY);
            localStorage.removeItem(SHOULD_RESTORE_KEY);
            localStorage.removeItem('catalogueSelectedRow');
        },
        []);

    // Trigger restoration when loading completes and data is available
    React.useEffect(
        () => {
            // Only restore when loading has finished and we have data
            if (isLoading || dataLength === 0) {
                return;
            }

            const shouldRestore = localStorage.getItem(SHOULD_RESTORE_KEY) === 'true';
            const savedPosition = localStorage.getItem(SCROLL_STORAGE_KEY);

            if (!shouldRestore || !savedPosition) {
                return;
            }

            // Wait for TreeList to be ready
            const checkAndRestore = () => {
                if (!treeListRef.current?.instance) {
                    setTimeout(checkAndRestore, DOM_CHECK_INTERVAL);

                    return;
                }

                const instance = treeListRef.current.instance;
                const numericScrollPosition = Number(savedPosition) || 0;

                const restoreScrollPosition = () => {
                    try {
                        if (numericScrollPosition > 0) {
                            const scrollable = instance.getScrollable();
                            scrollable.scrollTo({ top: numericScrollPosition });
                        }

                        // Clear restoration flags
                        localStorage.removeItem(SHOULD_RESTORE_KEY);
                    } catch (err) {
                        // Silently handle errors and clear restoration flags
                        localStorage.removeItem(SHOULD_RESTORE_KEY);
                    }
                };

                // Give minimal time for DOM to stabilize after data load
                setTimeout(restoreScrollPosition, DOM_STABILIZE_DELAY);
            };

            // Start the restoration process immediately after loading completes
            checkAndRestore();
        },
        [isLoading, dataLength, treeListRef]); // Depend on loading state and data

    return {
        saveScrollPosition,
        resetScrollState
    };
};
